import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../shared/models/user';
import {Role} from '../../shared/models/role';
import {AuthService} from '../../login/services/auth.service';
import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { NavBarItem } from 'app/theme/models/nav-bar-item';
import { Subject } from 'rxjs/Subject';
import { DacConfig } from '../../shared/services/dac-config.service';
import { Capability } from 'app/shared/models/capability';
import { ISubscription } from 'rxjs/Subscription';

@Injectable()
export class NavBarService implements OnDestroy {
  private _navItems: NavBarItem[];
  private user:User;
  private _displaySideBarEvent:Subject<boolean>;
  private _reportNavBarItems: NavBarItem[];

  /**
   * Represents the top navigation bar.
   */
  private _topNavBarItems: NavBarItem[];

  private _navBarAction:Subject<NavBarItem>;

  private _userSubscription:ISubscription;

  constructor(private authService:AuthService, private activatedRoute:ActivatedRoute, private router:Router, private dacConfig:DacConfig) { 

    this._userSubscription = this.authService.getLoginState().subscribe((stateUpdate) => {
      this.user = stateUpdate.user;
      this._navItems = this.createNavItems(); // update our navigation items.
      this._topNavBarItems = this.createTopNavItems();
    });
    // this._navItems = this.createNavItems();
    this._displaySideBarEvent = new Subject<boolean>();
    this._navBarAction = new Subject<NavBarItem>();
    this._reportNavBarItems = [];
  }

  ngOnDestroy() {
      this._userSubscription.unsubscribe();
  }


	public get displaySideBarEvent(): Subject<boolean> {
		return this._displaySideBarEvent;
  }
  
  public get navBarAction() : Subject<NavBarItem> {
    return this._navBarAction;
  }

  public toggleSidebar() {
    // send off an event to show the sidebar.
    this.displaySideBarEvent.next();
  }

  public setupReportSidebar(reportClientId) {
    let navItems = [];
    navItems.push(this.getHomeMenuItem());
     navItems.push({
      id: "clients"
      ,name: "Clients"
      ,routerLink: ['/','admin', 'clients']
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    navItems.push({
      id: "currentClient"
      ,name: 'Back to ' + reportClientId
      ,routerLink: ['/','admin', 'client', reportClientId]
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    this._reportNavBarItems = navItems;
  }

  public clearReportSidebar() {
    this._reportNavBarItems = [];
  }

  public addReportNavBarItem(id:string, name:string) {
    this._reportNavBarItems.push({
      id: id
      ,name: name
      ,routerLink: null
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,hasChildren: false
      ,type: 'report-item'
    });
  }

  public handleMenuItemClick(navItem:NavBarItem, event:Event) {
    if (navItem.id == 'logout') {
      event.preventDefault();
      event.stopPropagation();
      this.authService.logout().then((resp) => {
        if (resp.redirectUrl) {
          window.location.href = resp.redirectUrl;
        }
        else {
          this.router.navigate(['/home']);
        }
      })
      .catch((error) => {
        console.error(error);
        this.router.navigate(["/home"]);
      });
      return;
    }

    this._navBarAction.next(navItem);

    if (navItem.type && navItem.type == 'report-item') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public get topNavBarItemsForDisplay() {
    let items = this._topNavBarItems;
    // any item with children... filter out the children based upon the display
    items.filter(i => i.children)
    .forEach(i => {
      i.children = i.children.filter(ic => this.shouldDisplayItem(ic))
      i.hasChildren = i.children.length > 0;
    });

    // now filter out the parents.
    return items.filter(ni => this.shouldDisplayItem(ni));
  }

  private createTopNavItems() {
    let navItems = [
      this.getClientLoginMenuItem()
      , this.getAdminLoginMenuItem()
      , this.getClientDashboardMenuItem()];
    
    navItems.push({
      id: "help"
      ,name: "Contact"
      ,routerLink: ['/help']
      ,role: []
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    navItems.push(this.getMyAccountMenuItem());
    navItems.push(this.getLogoutMenuItem());
    return navItems;
  }

  private createNavItems() {
   let navItems = [];
   navItems.push(this.getHomeMenuItem());
    navItems.push(this.getClientLoginMenuItem());
    navItems.push(this.getAdminLoginMenuItem());
    navItems.push(this.getClientDashboardMenuItem());
    navItems.push({
      id: "systemOverview"
      ,name: "System Overview"
      ,routerLink: ['/','admin','system']
      ,role: [Role.SuperUser]
      ,roleExplicitMatch: true
      ,hasChildren: false
    });
    navItems.push({
      id: "companies"
      ,name: "Companies"
      ,routerLink: ['/','admin', 'companies']
      ,role: [Role.SuperUser]
      ,roleExplicitMatch: true
      ,hasChildren: false
    });
    navItems.push({
      id: "users"
      ,name: "Users"
      ,routerLink: ['/','admin', 'users']
      ,role: [Role.Admin]
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    navItems.push({
      id: "clients"
      ,name: "Clients"
      ,routerLink: ['/','admin', 'clients']
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    
    let assessments = {
      id: "adminAssessments"
      ,name: "Assessments"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,children: []
      ,hasChildren: true
    };
    assessments.children.push({
      id: "manageAssessments"
      ,name: "Assessments"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,routerLink: ['/','admin', 'assessment', 'manage']
      ,hasChildren: false
    });
    assessments.children.push({
      id: "manageBatteries"
      ,name: "Batteries"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,routerLink: ['/','admin', 'assessment', 'batteries']
      ,hasChildren: false
    });
    if (this.dacConfig.isFeatureEnabled('assessments.reports.manage')) {
      assessments.children.push({
        id: "manageAssessmentReports"
        ,name: "Reports"
        ,role: [Role.SuperUser]
        ,roleExplicitMatch: false
        ,routerLink: ['/','admin', 'assessment', 'custom-reports']
        ,hasChildren: false
      });
    }

    navItems.push(assessments);

    let library = {
      id: "adminLibrary"
      ,name: "Homework Library"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,children: []
      ,hasChildren: true
    };
    library.children.push({
      id: "libraryArticles"
      ,name: "Articles"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,children: []
      ,hasChildren: false
      ,routerLink: ['/','admin', 'library', 'articles']
    });
    library.children.push({
      id: "libraryExercises"
      ,name: "Exercises"
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,children: []
      ,hasChildren: false
      ,routerLink: ['/','admin', 'library', 'exercises']
    });

    navItems.push(library);

    navItems.push({
      id: "shop"
      ,name: "Shop"
      ,routerLink: ['/','admin', 'shop']
      ,role: []
      ,capability: [Capability.CAN_SHOP]
      ,roleExplicitMatch: false
      ,hasChildren: false
    });
    navItems.push(this.getMyAccountMenuItem());
    navItems.push(this.getLogoutMenuItem());
    return navItems;

  }

  private getClientDashboardMenuItem() {
    return {
      id: "clientDashboard"
      ,name: "Dashboard"
      ,routerLink: ['/','assessments','dashboard']
      ,role: [Role.Client]
      ,roleExplicitMatch: true
      ,hasChildren: false
    };
  }

  private getHomeMenuItem() {
    return {
      id: "home"
       ,name: "Home"
       ,routerLink: ['/']
       ,role: [Role.UnAuthenticated]
       ,roleExplicitMatch: false
       ,hasChildren: false
     };
  }

  private getClientLoginMenuItem() {
    return {
      id: "clientLogin"
      ,name: "Client Login"
      ,routerLink: ['/login']
      ,role: [Role.UnAuthenticated]
      ,roleExplicitMatch: true
      ,hasChildren: false
    };
  }
  private getAdminLoginMenuItem() {
    return {
      id: "adminLogin"
      ,name: "Therapist Login"
      ,routerLink: ['/loginAdmin/']
      ,role: [Role.UnAuthenticated]
      ,roleExplicitMatch: true
      ,hasChildren: false
    };
  }
  private getLogoutMenuItem() {
    return {
      id: "logout"
      ,name: "Logout"
      ,routerLink: null // no link as we don't want to go to a logout right now.
      ,role: [Role.Client]
      ,roleExplicitMatch: false
      ,hasChildren: false
    };
  }

  private getMyAccountMenuItem() {
    return {
      id: "adminMyAccount"
      ,name: "My Account"
      ,routerLink: ['/','admin', 'my-account']
      ,role: [Role.Registered]
      ,roleExplicitMatch: false
      ,hasChildren: false
    };
  }
  public shouldDisplayItem(navItem:NavBarItem) {
    if (navItem.capability && navItem.capability.length) {
      return navItem.capability.some(c => this.user.hasCapability(c));
    }
    
    if (navItem.role.length) {
      if (navItem.roleExplicitMatch) {
        return navItem.role.some(r => this.user.role === r);
      }
      else {
        return navItem.role.some(r => this.user.role <= r);
      }
    }
    return true;
  }

  public get navItemsForDisplay() {
    let items = this.navItems;
    if (this._reportNavBarItems.length) {
      items = this._reportNavBarItems;
    }
    // any item with children... filter out the children based upon the display
    items.filter(i => i.children)
    .forEach(i => {
      i.children = i.children.filter(ic => this.shouldDisplayItem(ic))
      i.hasChildren = i.children.length > 0;
    });

    // now filter out the parents.
    return items.filter(ni => this.shouldDisplayItem(ni));
  }


  public get navItems() : NavBarItem[] {
    return this._navItems;
  }

}



// WEBPACK FOOTER //
// ./src/app/theme/services/nav-bar.service.ts